import { useState, type ReactNode } from 'react';
import {
  BookOpen,
  Download,
  IndianRupee,
  Layers,
  MessageSquare,
  PencilLine,
  Printer,
  Receipt,
  School,
  Wallet,
} from 'lucide-react';

import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Modal } from '@/components/ui/Modal';
import { QueryError } from '@/components/ui/QueryError';
import { Skeleton } from '@/components/ui/Skeleton';
import { useApiQuery } from '@/hooks/useApiQuery';
import { formatCurrency, formatDate } from '@/lib/format';
import type { FeeHeadLedger, FeeReceipt, StudentFeeAccount } from '@/types/fees';
import { feesApi } from '../api/fees.api';
import { FEE_METHOD_LABEL } from '../constants';
import { CancelReceiptDialog } from './CancelReceiptDialog';
import { FeeReceiptPrint, type PrintableReceipt } from './FeeReceiptDocument';
import { InstallmentScheduleList } from './InstallmentScheduleList';
import { LedgerFigures } from './LedgerFigures';

export interface StudentFeeAccountModalProps {
  /** Whose account to show, or null while the dialog is closed. */
  studentUuid: string | null;
  isOpen: boolean;
  onClose: () => void;
  canCollect: boolean;
  canManage: boolean;
  canRemind: boolean;
  onCollect: (account: StudentFeeAccount) => void;
  onEditCharge: (account: StudentFeeAccount) => void;
  onRemind: (account: StudentFeeAccount) => void;
  /** The register behind this dialog needs to redraw after a cancellation. */
  onChanged: () => void;
}

/**
 * One student's account: what they were charged, and every receipt against it.
 *
 * A dialog rather than a page of its own, because it is read *from* the
 * register — an office works down a list of defaulters and opens each in turn,
 * and a full navigation would lose the filters they set to build that list.
 *
 * Cancelled receipts are shown, struck through and labelled. They are part of
 * the record: a reversal that vanished would leave a family's paperwork holding
 * a receipt this screen denies exists.
 */
export function StudentFeeAccountModal({
  studentUuid,
  isOpen,
  onClose,
  canCollect,
  canManage,
  canRemind,
  onCollect,
  onEditCharge,
  onRemind,
  onChanged,
}: StudentFeeAccountModalProps) {
  const [cancelling, setCancelling] = useState<FeeReceipt | null>(null);
  /** The receipt on its way to the printer, or null. */
  const [printing, setPrinting] = useState<PrintableReceipt | null>(null);

  const { data, isLoading, error, refetch } = useApiQuery(
    () => (studentUuid ? feesApi.getAccount(studentUuid) : Promise.resolve(null)),
    [studentUuid],
  );

  /** The receipt, plus who it was issued to — everything the paper names. */
  function printableFor(receipt: FeeReceipt): PrintableReceipt | null {
    if (!data) return null;

    return {
      receipt,
      student: {
        fullName: data.student.fullName,
        admissionNumber: data.student.admissionNumber,
        className: data.student.className,
        sectionName: data.student.sectionName,
        rollNumber: data.student.rollNumber,
      },
      sessionName: data.academicYear.name,
    };
  }

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="lg"
        title={data?.student.fullName ?? 'Fee account'}
        description={
          data
            ? [
                data.student.admissionNumber,
                data.student.className && `${data.student.className} ${data.student.sectionName}`,
                data.academicYear.name,
              ]
                .filter(Boolean)
                .join(' · ')
            : undefined
        }
        footer={
          <div className="flex items-center gap-2.5 flex-wrap">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-200/80 dark:border-slate-700 px-4 py-2 text-sm font-bold shadow-2xs transition-all cursor-pointer inline-flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
            >
              Close
            </button>
            {/* Offered here as well as on the register row, because this is
                where somebody ends up after reading what a family owes — and
                only while they actually owe it. */}
            {canRemind && data?.hasAccount && Number(data.balance) > 0 && (
              <button
                type="button"
                onClick={() => onRemind(data)}
                className="rounded-xl bg-indigo-50 dark:bg-indigo-950/60 hover:bg-indigo-100 dark:hover:bg-indigo-900/60 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800/80 px-4 py-2 text-sm font-bold shadow-2xs transition-all cursor-pointer inline-flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
              >
                <MessageSquare className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                <span>Remind</span>
              </button>
            )}
            {canManage && data && (
              <button
                type="button"
                onClick={() => onEditCharge(data)}
                className="rounded-xl bg-white dark:bg-[#111c2e] hover:bg-slate-50 dark:hover:bg-[#16253d] text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white border border-slate-200 dark:border-slate-700 px-4 py-2 text-sm font-bold shadow-2xs transition-all cursor-pointer inline-flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
              >
                <PencilLine className="h-4 w-4 text-slate-500 dark:text-slate-400" />
                <span>{data.hasAccount ? 'Edit fees' : 'Set fees'}</span>
              </button>
            )}
            {canCollect && data?.hasAccount && Number(data.balance) > 0 && (
              <button
                type="button"
                onClick={() => onCollect(data)}
                className="rounded-xl bg-indigo-600 tracking-wide hover:bg-indigo-700 text-white px-4 py-2 text-sm font-bold inline-flex items-center gap-2 transition-all cursor-pointer shadow-sm shadow-indigo-500/20 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
              >
                <IndianRupee className="h-4 w-4 stroke-[2.5]" />
                <span>Receive fee</span>
              </button>
            )}
          </div>
        }
      >
        {isLoading && (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        )}

        {error && (
          <QueryError title="Could not load this account" error={error} onRetry={refetch} />
        )}

        {data && !isLoading && (
          <div className="flex flex-col gap-4">
            {!data.hasAccount && (
              <EmptyState
                icon={<Wallet className="h-5 w-5" />}
                title="No fees set for this session"
                description={
                  canManage
                    ? 'Nothing can be collected until somebody says what this student is charged.'
                    : 'Nothing can be collected yet. Ask the accounts office to set the fees.'
                }
                action={
                  canManage ? (
                    <Button onClick={() => onEditCharge(data)}>Set the fees</Button>
                  ) : undefined
                }
              />
            )}

            {/*
              Who the institute would ring, and who a reminder would reach.
              On the account rather than on the register: this is one family,
              and it is the moment somebody is deciding whether to chase them.
            */}
            {data.student.contact && (
              <p className="flex flex-wrap items-baseline gap-x-2 text-sm">
                <span className="lbl">{data.student.contact.relationship}</span>
                <span className="text-ink">{data.student.contact.name}</span>
                <span className="font-mono text-xs tabular-nums text-ink-3">
                  {data.student.contact.mobile ?? 'No number on record'}
                </span>
              </p>
            )}

            {data.hasAccount && (
              <>
                <LedgerFigures ledger={data} status={data.status} />

                <div className="grid gap-3 sm:grid-cols-3">
                  <HeadCard
                    title="Tuition"
                    icon={<School className="h-3.5 w-3.5" aria-hidden />}
                    head={data.tuition}
                  />
                  <HeadCard
                    title="Books and materials"
                    icon={<BookOpen className="h-3.5 w-3.5" aria-hidden />}
                    head={data.books}
                  />
                  <HeadCard
                    title="Other fees"
                    icon={<Layers className="h-3.5 w-3.5" aria-hidden />}
                    head={data.other}
                  />
                </div>

                {data.concessionReason && (
                  <p className="text-sm text-ink-2">
                    <span className="lbl">Concession</span>{' '}
                    <span className="text-ink">{data.concessionReason}</span>
                  </p>
                )}

                {data.installments.length > 0 && (
                  <InstallmentScheduleList installments={data.installments} />
                )}
              </>
            )}

            <section>
              <h3 className="lbl mb-2">Receipts</h3>

              {data.receipts.length === 0 ? (
                <EmptyState
                  icon={<Receipt className="h-5 w-5" />}
                  title="Nothing collected yet"
                  description="Every payment taken against this account appears here with its receipt number."
                />
              ) : (
                <ul className="flex flex-col gap-2">
                  {data.receipts.map((receipt) => (
                    <li
                      key={receipt.uuid}
                      className="rounded-card border border-line bg-surface-2 p-3"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="flex flex-wrap items-center gap-2">
                            <span className="font-mono text-sm tabular-nums text-ink">
                              {receipt.receiptNumber}
                            </span>
                            {receipt.isCancelled && <Badge tone="critical">Cancelled</Badge>}
                          </p>
                          <p className="mt-0.5 text-xs text-ink-3">
                            {formatDate(receipt.paidOn)} · {FEE_METHOD_LABEL[receipt.method]}
                            {receipt.reference && ` · ${receipt.reference}`} · taken by{' '}
                            {receipt.collectedBy}
                          </p>
                        </div>

                        <div className="text-right">
                          <p
                            className={
                              receipt.isCancelled
                                ? 'font-mono text-base tabular-nums text-ink-3 line-through'
                                : 'font-mono text-base tabular-nums text-ink'
                            }
                          >
                            {formatCurrency(receipt.amount)}
                          </p>

                          {/*
                            A cancelled receipt is printable too, and on
                            purpose: it comes off the paper marked cancelled,
                            which is what an office needs when a family turns
                            up holding the copy it was handed at the counter.
                          */}
                          <div className="mt-1 flex flex-wrap items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              leftIcon={<Printer className="h-4 w-4" />}
                              aria-label={`Print receipt ${receipt.receiptNumber}`}
                              onClick={() => setPrinting(printableFor(receipt))}
                            >
                              Print
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              leftIcon={<Download className="h-4 w-4" />}
                              aria-label={`Download receipt ${receipt.receiptNumber}`}
                              title="Choose “Save as PDF” in the print dialog"
                              onClick={() => setPrinting(printableFor(receipt))}
                            >
                              Download
                            </Button>
                            {canCollect && !receipt.isCancelled && (
                              <Button
                                variant="dangerSoft"
                                size="sm"
                                onClick={() => setCancelling(receipt)}
                              >
                                Cancel
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>

                      {receipt.remarks && (
                        <p className="mt-2 text-sm text-ink-2">{receipt.remarks}</p>
                      )}
                      {receipt.isCancelled && (
                        <p className="mt-2 text-sm text-crit">
                          Cancelled by {receipt.cancelledBy} — {receipt.cancellationReason}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        )}
      </Modal>

      {/*
        No toast here, deliberately: this dialog stays open behind the cancel
        one, and a toast under an open `<dialog>` sits behind it in the
        browser's top layer. The ledger below redraws instead — the receipt
        struck through, badged "Cancelled", with the reason under it — which
        says more than a message that fades.
      */}
      <CancelReceiptDialog
        receipt={cancelling}
        onCancelled={() => {
          setCancelling(null);
          refetch();
          onChanged();
        }}
        onDismiss={() => setCancelling(null)}
      />

      {/* Outside the dialog above in the DOM as well as in the markup — the
          document is portalled to the body, so this dialog stays open and
          untouched behind the print. */}
      <FeeReceiptPrint target={printing} onFinished={() => setPrinting(null)} />
    </>
  );
}

/** One head of the bill, as the account screen reads it. */
function HeadCard({
  title,
  icon,
  head,
}: {
  title: string;
  icon: ReactNode;
  head: FeeHeadLedger;
}) {
  const balance = Number(head.balance);

  return (
    <div className="rounded-card border border-line p-3">
      <h4 className="lbl flex items-center gap-1.5 text-accent">
        {icon}
        {title}
      </h4>
      <dl className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm">
        <div className="flex gap-1.5">
          <dt className="text-ink-3">Payable</dt>
          <dd className="font-mono tabular-nums text-ink">{formatCurrency(head.payable)}</dd>
        </div>
        <div className="flex gap-1.5">
          <dt className="text-ink-3">Paid</dt>
          <dd className="font-mono tabular-nums text-ok">{formatCurrency(head.paid)}</dd>
        </div>
        <div className="flex gap-1.5">
          <dt className="text-ink-3">Balance</dt>
          <dd
            className={
              balance > 0
                ? 'font-mono tabular-nums text-crit'
                : 'font-mono tabular-nums text-ink-2'
            }
          >
            {formatCurrency(head.balance)}
          </dd>
        </div>
      </dl>
    </div>
  );
}
